
import React from 'react';
import { Link } from 'react-router-dom';
import { Share2, Clock, MapPin, Calendar, Zap, Users, Home, RotateCcw, Smartphone } from 'lucide-react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import Card from '../../components/base/Card';
import { appStoreUrlDiagnosisResultCta, googlePlayUrlDiagnosisResultCta } from '@/utils/appStoreUtm';

export default function DiagnosisResult() {
  const handleShare = () => {
    const shareText = `私の旅行MBTIは「完璧弾丸トラベラー(PALE型)」でした！⚡📋\n\nGoogle Mapに行程を秒単位で登録する効率重視の旅行者です。\n\nあなたも診断してみませんか？`;
    
    if (navigator.share) {
      navigator.share({
        title: '旅行MBTI診断結果 - 完璧弾丸トラベラー',
        text: shareText,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(shareText + '\n' + window.location.href);
      alert('結果をクリップボードにコピーしました！');
    }
  };

  return (
    <>
      <Header />
      <main className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#EEF2FF] to-[#F1F5F9] py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] text-white mb-6 shadow-lg">
              <Zap className="w-10 h-10" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#111827] mb-4">
              診断結果
            </h1>
            <p className="text-xl text-[#6B7280]">
              あなたの旅行MBTIが判明しました
            </p>
          </div>

          {/* Main Result Card */}
          <Card className="p-8 mb-8 bg-white/95 backdrop-blur-sm shadow-2xl border-0">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              {/* Left Side - Type Info */}
              <div className="text-center lg:text-left">
                <div className="mb-6">
                  <div className="flex items-center justify-center lg:justify-start mb-4">
                    <div className="text-6xl mr-4">⚡</div>
                    <div className="text-6xl">📋</div>
                  </div>
                  <p className="text-lg text-[#6B7280] mb-2">あなたの旅行MBTI:</p>
                  <h2 className="text-4xl md:text-5xl font-bold text-[#111827] mb-4 leading-tight">
                    完璧弾丸トラベラー
                  </h2>
                  <div className="inline-block bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] text-white px-8 py-3 rounded-full text-2xl font-bold shadow-lg mb-6">
                    PALE型
                  </div>
                  <div className="flex items-center justify-center lg:justify-start mb-6">
                    <div className="flex items-center bg-[#FEF3C7] text-[#92400E] px-4 py-2 rounded-full">
                      <span className="text-lg mr-2">希少度:</span>
                      <div className="flex">
                        {[1,2,3].map(i => (
                          <span key={i} className="text-[#F59E0B] text-xl">★</span>
                        ))}
                        {[4,5].map(i => (
                          <span key={i} className="text-[#D1D5DB] text-xl">☆</span>
                        ))}
                      </div>
                      <span className="ml-2 font-bold">15%</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-4 text-lg text-[#6B7280] leading-relaxed mb-6 bg-[#F8FAFC] p-6 rounded-2xl">
                  <p className="font-semibold text-[#111827]">「効率こそ正義！」がモットー</p>
                  <p>Google Mapに行程を秒単位で登録し、旅行の3ヶ月前から天気予報をチェック開始。</p>
                  <p>現地の営業時間を全て把握済みで、旅行というより軍事作戦レベルの計画性。</p>
                  <p className="text-[#EF4444] font-medium">でも帰国後の疲労度は最高レベル...</p>
                </div>
              </div>

              {/* Right Side - Character Illustration */}
              <div className="flex justify-center">
                <img 
                  src="/images/prae-traveler-main.jpg"
                  alt="完璧弾丸トラベラーキャラクター"
                  className="w-full max-w-md rounded-2xl shadow-xl object-top"
                />
              </div>
            </div>
          </Card>

          {/* Travel Habits (replaced) */}
          <div className="rounded-2xl shadow-sm transition-all duration-200 bg-white p-8 mb-8 bg-white/95 backdrop-blur-sm shadow-xl">
            <h3 className="text-3xl font-bold text-[#111827] mb-8 text-center flex items-center justify-center">
              <Clock className="w-8 h-8 mr-3 text-[#3B82F6]" />
              旅あるある
            </h3>
            
            <div className="max-w-3xl mx-auto">
              <ul className="space-y-4 text-lg">
                <li className="flex items-start">
                  <div className="w-2 h-2 rounded-full bg-[#3B82F6] mt-3 mr-4 flex-shrink-0"></div>
                  <span className="text-[#374151]">「17:23の電車に乗れば17:56に到着して...」と秒単位で語る</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 rounded-full bg-[#3B82F6] mt-3 mr-4 flex-shrink-0"></div>
                  <span className="text-[#374151]">スケジュールが5分遅れるとパニック</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 rounded-full bg-[#3B82F6] mt-3 mr-4 flex-shrink-0"></div>
                  <span className="text-[#374151]">同行者に「もっとゆっくりしようよ...」と言われがち</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 rounded-full bg-[#3B82F6] mt-3 mr-4 flex-shrink-0"></div>
                  <span className="text-[#374151]">帰国後2週間は動けない</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Recommendations */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            <Card className="p-8 bg-white/95 backdrop-blur-sm shadow-xl">
              <h3 className="text-2xl font-bold text-[#111827] mb-6 flex items-center">
                <MapPin className="w-6 h-6 mr-3 text-[#3B82F6]" />
                おすすめ旅行先
              </h3>
              
              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-bold text-[#111827] mb-4 flex items-center">
                    <span className="text-2xl mr-2">🇯🇵</span>
                    国内
                  </h4>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-4 p-4 rounded-xl bg-gradient-to-r from-[#3B82F6]/10 to-[#8B5CF6]/10 border-l-4 border-[#3B82F6]">
                      <img 
                        src="/images/tokyo-efficient-001.jpg"
                        alt="東京"
                        className="w-16 h-16 rounded-lg object-cover object-top"
                      />
                      <div>
                        <h5 className="font-bold text-[#111827]">東京</h5>
                        <p className="text-sm text-[#6B7280]">効率的な交通網で秒単位の移動が可能</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4 p-4 rounded-xl bg-gradient-to-r from-[#3B82F6]/10 to-[#8B5CF6]/10 border-l-4 border-[#3B82F6]">
                      <img 
                        src="/images/kyoto-temples-001.jpg"
                        alt="京都"
                        className="w-16 h-16 rounded-lg object-cover object-top"
                      />
                      <div>
                        <h5 className="font-bold text-[#111827]">京都</h5>
                        <p className="text-sm text-[#6B7280]">寺社30ヶ所制覇プランが立てられる</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-lg font-bold text-[#111827] mb-4 flex items-center">
                    <span className="text-2xl mr-2">🌏</span>
                    海外
                  </h4>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-4 p-4 rounded-xl bg-gradient-to-r from-[#10B981]/10 to-[#34D399]/10 border-l-4 border-[#10B981]">
                      <img 
                        src="/images/singapore-efficient-001.jpg"
                        alt="シンガポール"
                        className="w-16 h-16 rounded-lg object-cover object-top"
                      />
                      <div>
                        <h5 className="font-bold text-[#111827]">シンガポール</h5>
                        <p className="text-sm text-[#6B7280]">時間通りに動く完璧な国</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4 p-4 rounded-xl bg-gradient-to-r from-[#10B981]/10 to-[#34D399]/10 border-l-4 border-[#10B981]">
                      <img 
                        src="/images/swiss-transport-001.jpg"
                        alt="スイス"
                        className="w-16 h-16 rounded-lg object-cover object-top"
                      />
                      <div>
                        <h5 className="font-bold text-[#111827]">スイス</h5>
                        <p className="text-sm text-[#6B7280]">完璧な公共交通システム</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="p-8 bg-white/95 backdrop-blur-sm shadow-xl">
              <h3 className="text-2xl font-bold text-[#111827] mb-6 flex items-center">
                <Users className="w-6 h-6 mr-3 text-[#8B5CF6]" />
                相性診断
              </h3>
              
              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-bold text-[#10B981] mb-4 flex items-center">
                    <span className="text-2xl mr-2">✅</span>
                    相性良好
                  </h4>
                  <div className="p-6 bg-gradient-to-r from-[#10B981]/10 to-[#34D399]/10 rounded-2xl border-l-4 border-[#10B981]">
                    <div className="flex items-center justify-between mb-3">
                      <h5 className="text-xl font-bold text-[#111827]">PALE型</h5>
                      <div className="flex">
                        {[1,2,3,4,5].map(i => (
                          <span key={i} className="text-[#10B981] text-xl">★</span>
                        ))}
                      </div>
                    </div>
                    <p className="text-[#6B7280] mb-2">同じく計画的なタイプ</p>
                    <p className="text-sm text-[#6B7280]">お互いの計画性を理解し合える最高のパートナー。スケジュール共有も完璧！</p>
                  </div>
                </div>

                <div>
                  <h4 className="text-lg font-bold text-[#EF4444] mb-4 flex items-center">
                    <span className="text-2xl mr-2">❌</span>
                    相性注意
                  </h4>
                  <div className="p-6 bg-gradient-to-r from-[#EF4444]/10 to-[#F87171]/10 rounded-2xl border-l-4 border-[#EF4444]">
                    <div className="flex items-center justify-between mb-3">
                      <h5 className="text-xl font-bold text-[#111827]">SRCZ型</h5>
                      <div className="flex">
                        <span className="text-[#EF4444] text-xl">★</span>
                        {[2,3,4,5].map(i => (
                          <span key={i} className="text-[#D1D5DB] text-xl">☆</span>
                        ))}
                      </div>
                    </div>
                    <p className="text-[#6B7280] mb-2">正反対すぎて発狂レベル</p>
                    <p className="text-sm text-[#6B7280]">「今日はどこ行く？」と当日に聞かれて血圧上昇。事前の心の準備が必要。</p>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-[#02D0F0] to-[#0EA5E9] rounded-2xl p-8 text-white text-center">
            <h3 className="text-3xl font-bold mb-4">あなたにぴったりの旅を見つけよう</h3>
            <p className="text-lg mb-6 opacity-90">
              診断結果をもとに、最適な旅行プランをご提案します
            </p>
            <a href={appStoreUrlDiagnosisResultCta('1')} target="_blank" rel="noopener noreferrer"><Button 
              size="lg" 
              className="bg-white text-[#02D0F0] hover:bg-gray-100 font-semibold px-8 py-3"
            >
              旅行プランを探す
            </Button>
                  </a>
                <a href={googlePlayUrlDiagnosisResultCta('1')} target="_blank" rel="noopener noreferrer"><Button 
                  variant="outline" 
                  className="border-white text-white hover:bg-white/90 font-semibold"
                >
                  <i className="ri-google-play-fill mr-2 text-xl" aria-hidden />
                  Google Playでダウンロード
                </Button>
                  </a>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mt-8">
            <Button 
              variant="outline" 
              className="whitespace-nowrap shadow-lg hover:shadow-xl transition-all duration-300 border-2"
              onClick={handleShare}
            >
              <Share2 className="w-4 h-4 mr-2" />
              結果をシェア
            </Button>
            <Link to="/">
              <Button variant="outline" className="whitespace-nowrap shadow-lg hover:shadow-xl transition-all duration-300 border-2">
                <Home className="w-4 h-4 mr-2" />
                ホームに戻る
              </Button>
            </Link>
            <Link to="/diagnosis">
              <Button variant="outline" className="whitespace-nowrap shadow-lg hover:shadow-xl transition-all duration-300 border-2">
                <RotateCcw className="w-4 h-4 mr-2" />
                もう一度診断する
              </Button>
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
